import { memo } from "react";
import {
  BaseEdge,
  EdgeLabelRenderer,
  type Edge,
  type EdgeProps,
} from "@xyflow/react";
import {
  computeOrthogonalPath,
  midpoint,
  type Point,
  waypointsToSvgPathWithHops,
} from "./routing.ts";

export type OrthogonalWireEdgeData = {
  designation?: string;
  severity?: "ok" | "warning";
  // Mänskligt läsbart skäl som visas i tooltip när severity === "warning".
  // Källa: validateConnection från @electro-planner/domain, t.ex.
  // "Fas-mismatch: L1 mot L2." eller "Ovanlig koppling: phase → signal."
  reason?: string;
  // Ledarens tvärsnittsarea i mm² (1.5, 2.5, 4 …). Projekteras från
  // Conductor.attributes.area; undefined → ingen label-del.
  area?: number;
  // IEC-färgkod (BN, BK, GY, BU, GNYE, …) — också projekterad från conductor.
  // Vi använder en lookup nedan för att mappa kod till stroke-color.
  color?: string;
  // Kretsbeteckning, fri text (t.ex. "F1", "F1:L1"). Placeras först i
  // label-kedjan eftersom det är den viktigaste identifierande informationen
  // för en elektriker som läser ritningen.
  circuitLabel?: string;
  // Cable-beteckning (W1, W2…) när conductorn ingår i en kabel. Ritas först i
  // label-kedjan, inom hakparenteser, så kabeln syns tydligt för alla ledare
  // som ingår i den utan att skymma area/färg.
  cableDesignation?: string;
  // CableId — behövs för inspektören så rename/delete kan göras utan att gå
  // via designation (som inte är garanterat unik).
  cableId?: string;
  // Kabeltyp enligt tillverkarens nomenklatur (t.ex. "EKK 3G1.5", "FK 5G2.5").
  // Syns bara i inspektören — inte på ritningen — eftersom det är en metadata
  // för apparat-/kabellista, inte för visuell identifiering.
  cableType?: string;
  // Hop-punkter beräknade i projection-hooken. Edge:n ritar en halvcirkel-båge
  // vid varje hop — visuell line-break, elektriskt oförändrad.
  hops?: Point[];
};

export type OrthogonalWireEdgeType = Edge<OrthogonalWireEdgeData, "orthogonalWire">;

// IEC 60446 / SS-EN 60446-härledda färger för standardkoder.
// GNYE ritas som heldragen grön; strikt sett är den grön-gul strimlig men
// på skärm räcker grön för diff:bar avkänning.
const WIRE_COLOR_STROKE: Readonly<Record<string, string>> = {
  BN: "#8b4513", // brun
  BK: "#111827", // svart (default)
  GY: "#6b7280", // grå
  BU: "#1d4ed8", // blå
  GNYE: "#16a34a", // grön-gul (förenklad)
  RD: "#dc2626", // röd
  YE: "#ca8a04", // gul/orange
  WH: "#9ca3af", // vit (visas grå på ljus bakgrund)
  OG: "#ea580c", // orange
};

function resolveStroke(
  code: string | undefined,
  severity: OrthogonalWireEdgeData["severity"],
  selected: boolean,
): string {
  if (severity === "warning") return "var(--color-danger, #dc2626)";
  if (code) {
    const normalized = code.toUpperCase();
    const preset = WIRE_COLOR_STROKE[normalized];
    if (preset) return preset;
    // Okänd kod men användaren har skrivit något — använd det som hex/namn.
    return code;
  }
  if (selected) return "var(--color-primary, #0f766e)";
  return "var(--color-fg, #111827)";
}

function formatEdgeLabel(data: OrthogonalWireEdgeData | undefined): string | null {
  if (!data) return null;
  const parts: string[] = [];
  if (data.cableDesignation) parts.push(`[${data.cableDesignation}]`);
  if (data.circuitLabel) parts.push(data.circuitLabel);
  if (data.designation) parts.push(data.designation);
  if (typeof data.area === "number" && Number.isFinite(data.area)) {
    // Visa 1.5 / 2.5 / 4 — utan efterföljande "mm²" för att spara canvas-plats.
    // Vill användaren ha enhet syns den i inspector-panelen.
    parts.push(formatArea(data.area));
  }
  if (data.color) parts.push(data.color.toUpperCase());
  return parts.length > 0 ? parts.join(" · ") : null;
}

function formatArea(area: number): string {
  // 1.5 → "1.5", 4.0 → "4", 2.5 → "2.5"
  const rounded = Math.round(area * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function OrthogonalWireEdgeImpl(props: EdgeProps<OrthogonalWireEdgeType>) {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    selected,
    data,
  } = props;

  const points = computeOrthogonalPath(
    { x: sourceX, y: sourceY },
    { x: targetX, y: targetY },
    sourcePosition,
    targetPosition,
  );
  const d = waypointsToSvgPathWithHops(points, data?.hops);
  const mid = midpoint(points);

  const strokeWidth = selected ? 2.5 : 1.5;
  const stroke = resolveStroke(data?.color, data?.severity, selected ?? false);
  const label = formatEdgeLabel(data);
  const isWarning = data?.severity === "warning";
  const reason = data?.reason;

  return (
    <>
      <BaseEdge id={id} path={d} style={{ stroke, strokeWidth, fill: "none" }} />
      {label || isWarning ? (
        <EdgeLabelRenderer>
          <div
            className={
              isWarning
                ? "pointer-events-auto select-none rounded border border-red-300 bg-red-50 px-1 text-[10px] font-medium text-red-700 shadow-sm"
                : "pointer-events-none select-none rounded bg-white px-1 text-[10px] font-medium text-gray-700 shadow-sm"
            }
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${mid.x}px, ${mid.y}px)`,
            }}
            title={isWarning ? reason : undefined}
          >
            {isWarning ? `⚠ ${label ?? "fel"}` : label}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}

export const OrthogonalWireEdge = memo(OrthogonalWireEdgeImpl);
OrthogonalWireEdge.displayName = "OrthogonalWireEdge";
